// src/pages/product/brand/BrandList.tsx

import React, { useState } from "react";
import { EDataTable } from "../../../components/EDataTable";
import { IconButton, TextButton } from "../../../components/ButtonComponents";
import { TableActions } from "../../../components/TableActions";
import { Edit, Trash2, BadgeDollarSign, Image as ImageIcon, Plus } from "lucide-react"; 
import { BrandDialog } from "./BrandDialog"; 
import { toast } from "react-toastify";
  
import { useBrandStore } from "../../../store/brandStore";  
import { useConfirmModal } from "../../../hooks/useConfirmModal";
import { useApiErrorHandler } from "../../../hooks/useApiErrorHandler";
import { DataTableColumn, SimpleFilter } from "../../../types"; 
import { Brand } from "../../../types/brand";

export const BrandList = () => {
    const store = useBrandStore();
    const { createItem, updateItem, deleteItem } = store; 
    const { handleApiError } = useApiErrorHandler();
    
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [brandToEdit, setBrandToEdit] = useState<Brand | null>(null);

    const handleNew = () => {
        setBrandToEdit(null);
        setIsModalOpen(true);
    };

    const handleEdit = (brand: Brand) => {
        setBrandToEdit(brand);
        setIsModalOpen(true);
    };

    const { confirm, modal: confirmModal } = useConfirmModal();

    const handleDeleteClick = async (brand: Brand) => {
        const ok = await confirm(`${brand.name} markasını silmek istiyor musun? Bu markaya bağlı ürünler etkilenebilir.`);
        if (ok) {
            try {
                await deleteItem(brand.id);
                toast.success("Marka başarıyla silindi");
            } catch (error) {
                handleApiError(error); 
            }
        }
    };

const handleCloseModal = () => {
    setIsModalOpen(false);
    setBrandToEdit(null);
    // 💡 ÖNEMLİ: Dialog kapandığında (resim yüklenmiş olabilir) 
    // tablodaki verileri en güncel haliyle tekrar çek.
    store.fetchDataDynamic(); 
};

    const handleSaveBrand = async (brandData: any) => {
        try {
            console.log(brandData)
            if (brandToEdit) {
                await updateItem(brandToEdit.id, brandData);
                toast.success(`Marka Güncellendi: ${brandData.name}`);
            } else {
                await createItem(brandData);
                toast.success(`Yeni Marka Eklendi: ${brandData.name}`);
            }
            handleCloseModal();
        } catch (error) {
            handleApiError(error); 
        }
    };

    // Marka Logosu Template
    const logoBodyTemplate = (brand: Brand) => (
        <div className="flex items-center justify-center w-10 h-10 rounded border bg-gray-50 overflow-hidden">
            {brand.logoUrl ? (
                <img src={brand.logoUrl} alt={brand.name} className="object-contain w-full h-full" />
            ) : (
                <ImageIcon size={18} className="text-gray-300" />
            )}
        </div>
    );

    const actionBodyTemplate = (brand: Brand) => (
        <TableActions
            actions={[
                {
                    icon: Edit,
                    colorClass: "text-blue-600 hover:bg-blue-50",
                    title: "Düzenle",
                    onClick: () => handleEdit(brand)
                },
                {
                    icon: Trash2,
                    colorClass: "text-red-500 hover:bg-red-50",
                    title: "Sil",
                    onClick: () => handleDeleteClick(brand)
                }
            ]}
        />
    );

    const brandColumns: DataTableColumn<Brand>[] = [
        { 
            field: "logoUrl", 
            header: "Logo", 
            width: "80px", 
            body: logoBodyTemplate,
            align: "center"
        },
        { 
            field: "name", 
            header: "Marka Adı", 
            width: "300px",
            sortable: true, 
        },
        
        { 
            field: "actions", 
            header: "İşlemler", 
            width: "120px", 
            align: "center",
            body: actionBodyTemplate 
        },
    ];

    return (
        <div className="w-full">
            {confirmModal}
            <EDataTable<Brand> 
            tableKey="brand_list"
                useStore={() => store} 
                dataKey="id"
                title="Marka Yönetimi"
                placeholder="Marka Adı veya Kod Ara"
                searchFields={[
                    { field: "name", operator: "contains" as SimpleFilter["operator"] },
                    { field: "externalId", operator: "contains" as SimpleFilter["operator"] },
                ]}
                actions={
                    <TextButton onClick={handleNew} className="btn-new flex gap-2">
                         <Plus size={18}/> Yeni Marka Ekle
                    </TextButton>
                }
                columns={brandColumns}
            />

            <BrandDialog
                isOpen={isModalOpen}
                onClose={handleCloseModal}
                brand={brandToEdit}
                onSave={handleSaveBrand}
            />
        </div> 
    );
};